import { useState } from "react";
import { styles } from "../../../utils/styles/products-home-styles";
import { useI18n } from "@/i18n/I18nProvider";
import Box from "@mui/material/Box";
import dynamic from "next/dynamic";
import ProductMain from "@/components/products-revamp/product-main";
import ApplyProduct from "@/components/products-revamp/product-apply";
import CalcPopup from "@/components/products-revamp/CalcPopup";
import WhyContact from "@/components/products-revamp/why-contact";
import SectionTitle from "@/components/products-revamp/section-title";
import DynamicFAQSection from "@/components/DynamicFAQSection";
import StepsRow from "@/components/steps-row";
import PgWithImgSection from "@/components/products-revamp/pg-with-img-section";
import Features from "@/components/products-revamp/features-section";
import {
  getLocalizedValue,
  getStaticTextByKey,
} from "@/utils/general-functions";
import { emptyProps, getProductPageData } from "@/services/get-product-data";
import { useSplitContentSections } from "@/hooks/product";
import { useProductLoanPackages } from "@/hooks/useProductLoanPackages";
import { getProductsText } from "../../../../locales/products";
const PageHead = dynamic(() => import("@/components/PageHead"));

export default function Index({
  heroData,
  contentWithImageData,
  faqData,
  stepsData,
  whycontactData,
  staticTextData,
  featuresData,
  mainData,
  accordionData,
}) {
  const { t, locale } = useI18n();
  const products = getProductsText(locale);
  const [openCalc, setOpenCalc] = useState(false);
  const { targetProduct, data } = useProductLoanPackages(products.mortage.id);
  const staticTexts = getStaticTextByKey(staticTextData?.data);
  const { fullNavySections, splitSections } =
    useSplitContentSections(contentWithImageData);
  const middleIndex = Math.ceil(fullNavySections.length / 2);

  const firstFullNavySections = fullNavySections.slice(0, middleIndex);
  const secondFullNavySections = fullNavySections.slice(middleIndex);
  return (
    <>
      <PageHead
        title={getLocalizedValue(mainData[0], "meta_title", locale)}
        description={getLocalizedValue(mainData[0], "meta_description", locale)}
        scripts={getLocalizedValue(mainData[0], "schema", locale)}
      />

      <Box sx={{ ...styles.container, overflow: "hidden" }}>
        <ProductMain
          heroData={heroData}
          staticTexts={staticTexts}
          onApplyClick={() => {
            const formSection = document.getElementById("apply-form");
            if (formSection) {
              formSection.scrollIntoView({ behavior: "smooth" });
            }
          }}
          onCalcClick={() => setOpenCalc((prev) => !prev)}
          onExploreMoreClick={() => {
            const formSection = document.getElementById("mortgage-features");
            if (formSection) {
              formSection.scrollIntoView({ behavior: "smooth" });
            }
          }}
          imgStyle={{ mt: locale === "ar" ? 3 : { xs: 5, xl: 6 } }}
          headerH1={true}
        />
        {firstFullNavySections.map((section, index) => (
          <PgWithImgSection
            key={section.id || index}
            img={section.img}
            alt={section.alt_img}
            title={section.title?.text?.[locale]}
            titleType={section.title?.type}
            paragraph={section.description?.text?.[locale]}
            paragraphType={section.description?.type}
          />
        ))}

        <SectionTitle
          title={
            staticTexts?.mortgage_financing_title?.value.text[locale] || ""
          }
          description={
            staticTexts?.mortgage_financing_description?.value.text[locale] ||
            ""
          }
        />

        {secondFullNavySections.map((section, index) => (
          <PgWithImgSection
            key={section.id || index}
            img={section.img}
            alt={section.alt_img}
            title={section.title?.text?.[locale]}
            titleType={section.title?.type}
            paragraph={section.description?.text?.[locale]}
            paragraphType={section.description?.type}
          />
        ))}
        {featuresData?.length > 0 &&
          featuresData.map((section, index) => (
            <Features
              key={section?.id || index}
              section={section}
              bgHeight={{ xs: "71%", sm: "75%", md: "60%", lg: "50%" }}
              id="mortgage-features"
            />
          ))}

        <WhyContact whycontactData={whycontactData} />

        {/**faq section */}
        {faqData?.length > 0 &&
          faqData.map((section, index) => (
            <DynamicFAQSection
              key={section?.id || index}
              title={staticTexts?.faqs_section_title?.value?.text[locale] || ""}
              items={section?.faqs}
              renderHTML={true}
              variantTitle="h2"
              variantQ="h3"
            />
          ))}
        {/**Steps section */}
        {stepsData?.length > 0 &&
          stepsData.map((section, index) => (
            <StepsRow
              key={section.id || index}
              title={section?.title?.text?.[locale]}
              titleType={section?.title?.type}
              subtitle={section?.description?.text?.[locale]}
              subtitleType={section?.description?.type}
              steps={section?.items}
            />
          ))}

        <ApplyProduct
          img={heroData[0]?.img}
          title={mainData[0]?.form_title.text[locale]}
          titleType={mainData[0]?.form_title?.type}
          description={mainData[0]?.form_desc.text[locale]}
          descType={mainData[0]?.form_desc?.type}
          formType="mortgage"
          product="mortgage"
          source={32}
        />
        {targetProduct?.hasPlan && (
          <CalcPopup
            open={openCalc}
            onClose={() => setOpenCalc(false)}
            data={data}
            max_amount={targetProduct?.maxLimit}
            price_label={products.mortage.price}
          />
        )}
      </Box>
    </>
  );
}

export async function getServerSideProps() {
  try {
    const data = await getProductPageData("mortgage");
    return { props: data };
  } catch {
    return { props: emptyProps };
  }
}
